import React, { useEffect, useState } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import '@/shared/features/quiz-folder/consts/events.quiz-folder';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { LockIcon } from '@/icons';
import { useIsLoggerUserInfoSet } from '@/lib/auth';
import { useMoveQuizToDefault } from '@/shared/features/quiz-folder/hooks/useMoveQuizToDefault';
import { useShowSaveToFolderModal } from '@/shared/features/quiz-folder/hooks/useShowSaveToFolderModal';
import { useUpsellingGuard, useUserPlanManager } from '@/shared/plan';
import { BuildProFeatureCoachmarkAnchor } from '../components/BuildProFeatureCoachmark';
import { FeatureUpsellModal } from '../components/FeatureUpsellModal';
import { useFocusTarget } from '../components/focus/useFocusTarget';
import { useBuildInfoContext } from '../context';
import { useBuildFormContext } from '../form/schema';
import { PublicFolderMoveNotice } from './PublicFolderMoveNotice';
import { ToggleOptionContainer } from './ToggleOptionContainer';
export const TogglePublicField = () => {
  const {
    watch,
    setValue
  } = useBuildFormContext();
  const {
    t
  } = useTranslation(['build']);
  const {
    userPlanManager
  } = useUserPlanManager();
  const upsellingGuard = useUpsellingGuard();
  const {
    action,
    originQuizSet
  } = useBuildInfoContext();
  const showSaveToFolderModal = useShowSaveToFolderModal();
  const isPublic = watch('isPublic');
  const featurePlanType = userPlanManager.getFeaturePlanType('PRIVATE_QUIZ');
  const ref = useFocusTarget<HTMLDivElement>('isPublic');

  // 공개 폴더에는 공개 퀴즈만 담긴다. 그 안의 퀴즈는 먼저 위치를 옮겨야 숨길 수 있다.
  //
  // `UPDATE` 로 좁히는 이유: `originQuizSet` 은 복제(`DUPLICATE`)에도 채워지는데 그건 원본이다.
  const [hasMovedOut, setHasMovedOut] = useState(false);
  const currentFolderId = originQuizSet?.folderId;
  // 안내가 필요한 퀴즈. 공개 폴더 안의 `UPDATE` 대상일 때만 값이 있다.
  const publicFolderQuiz = action === 'UPDATE' && !hasMovedOut && !!currentFolderId && originQuizSet?.folderVisibility === 'PUBLIC' ? originQuizSet : undefined;

  // 옮긴 뒤에도 퀴즈 상세 쿼리를 다시 읽지 않는다 — refetch 가 편집 중인 내용을 되돌릴 수 있다.
  // 비공개 전환은 자동으로 켜지 않는다.
  const handleMovedOut = () => setHasMovedOut(true);

  // 편집 화면 진입 직후 안내가 뜨므로 유저 정보가 로거에 실린 뒤에 보낸다.
  const isLoggerUserInfoSet = useIsLoggerUserInfoSet();
  const noticeQuizId = publicFolderQuiz?.id;
  useEffect(() => {
    if (!noticeQuizId || !currentFolderId || !isLoggerUserInfoSet) return;
    trackEvent('quiz_folder_move_notice_view', {
      quiz_set_id: noticeQuizId,
      from_folder_id: currentFolderId
    });
  }, [noticeQuizId, currentFolderId, isLoggerUserInfoSet]);
  const moveToDefault = useMoveQuizToDefault({
    quizSetId: originQuizSet?.id ?? '',
    folderId: currentFolderId,
    place: 'build_setting_notice',
    onMoved: handleMovedOut,
    // 퀴즈 수정 화면을 벗어나지 않도록 `보러 가기` 는 새 탭으로 연다.
    openResultInNewTab: true
  });
  return <div ref={ref} className="flex flex-col gap-sm" tabIndex={0} data-sentry-component="TogglePublicField" data-sentry-source-file="TogglePublicField.tsx">
      <ToggleOptionContainer optionIcon={<LockIcon size="sm" fill={ICON_COLOR.neutral} />} optionTitle={t('build:hideFromExplore.title')} optionDescription={t('build:hideFromExplore.description')} isOn={!isPublic} planType={featurePlanType} isBadgeVisible={true} disabled={!!publicFolderQuiz} onToggle={() => {
      logger.track('isPublic_select', {
        value: !isPublic
      });
      upsellingGuard(() => {
        setValue('isPublic', !isPublic, {
          shouldDirty: true,
          shouldTouch: true
        });
      }, {
        feature: 'PRIVATE_QUIZ',
        from: 'isPublic',
        moreConditionForGuard: isPublic === true,
        onGuard: () => {
          overlay.open(({
            unmount
          }) => <FeatureUpsellModal feature="PRIVATE_QUIZ" onClose={unmount} />);
        }
      });
    }} renderToggle={toggle => <BuildProFeatureCoachmarkAnchor feature="PRIVATE_QUIZ">
            {toggle}
          </BuildProFeatureCoachmarkAnchor>} data-sentry-element="ToggleOptionContainer" data-sentry-source-file="TogglePublicField.tsx" />
      {publicFolderQuiz && <PublicFolderMoveNotice isMoving={moveToDefault.isMoving} onSelectFolder={() => void showSaveToFolderModal({
      id: publicFolderQuiz.id,
      intent: 'move',
      currentFolderId,
      place: 'build_setting_notice',
      isQuizOwner: true
    }, {
      privateOnly: true,
      onMoved: handleMovedOut,
      openResultInNewTab: true
    })} onMoveToMyQuiz={moveToDefault.move} />}
    </div>;
};